import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Check, X, Smartphone, Share } from 'lucide-react';
import { 
  requestNotificationPermission, 
  getNotificationPermission, 
  areNotificationsSupported,
  getNotificationSupportInfo,
  disableNotifications,
  onForegroundMessage
} from '../lib/notifications';

/**
 * Composant pour demander la permission des notifications push
 * Affiche un banner élégant en haut de l'app
 */
const NotificationPrompt = ({ user, activeCompanyId, onPermissionGranted }) => {
  const [permission, setPermission] = useState('default');
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!areNotificationsSupported()) return;
    
    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);
    
    // Afficher le prompt seulement si la permission n'a pas été donnée
    // et que l'utilisateur n'a pas explicitement refusé
    const hasDeclined = localStorage.getItem('notifications_declined');
    setIsVisible(currentPermission === 'default' && !hasDeclined);
  }, []);

  const handleEnable = async () => {
    if (!user || !activeCompanyId) return;
    
    setIsLoading(true);
    const granted = await requestNotificationPermission(user.uid, activeCompanyId);
    
    if (granted) {
      setPermission('granted');
      setIsVisible(false);
      if (onPermissionGranted) onPermissionGranted();
    } else {
      setPermission(Notification.permission);
    }
    
    setIsLoading(false);
  };

  const handleDecline = () => {
    localStorage.setItem('notifications_declined', 'true');
    setIsVisible(false);
  };

  if (!isVisible || !areNotificationsSupported()) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg animate-slide-down">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <Bell className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              Activez les notifications pour rester informé en temps réel
            </p>
            <p className="text-xs text-blue-100 mt-0.5">
              Nouveaux devis, signatures, messages... ne ratez plus rien !
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                Activation...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Activer
              </>
            )}
          </button>
          
          <button
            onClick={handleDecline}
            className="text-white hover:text-blue-100 p-2 rounded-lg hover:bg-blue-600 transition"
            title="Plus tard"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Composant de paramètres des notifications
 * À intégrer dans la page Settings
 */
export const NotificationSettings = ({ user, activeCompanyId }) => {
  const [permission, setPermission] = useState('default');
  const [isLoading, setIsLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    documentSigned: true,
    newDocument: true,
    siteCreated: true,
    eventCreated: true,
    eventReminder: true,
    invoiceDue: true,
    teamMessages: true
  });

  useEffect(() => {
    if (areNotificationsSupported()) {
      setPermission(getNotificationPermission());
    }
  }, []);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    const granted = await requestNotificationPermission(user.uid, activeCompanyId);
    setPermission(granted ? 'granted' : Notification.permission);
    setIsLoading(false);
  };

  const handleDisableNotifications = async () => {
    setIsLoading(true);
    await disableNotifications(user.uid);
    setPermission('denied');
    setIsLoading(false);
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    // TODO: Sauvegarder dans Firestore
  };

  if (!areNotificationsSupported()) {
    // Diagnostiquer la cause avec détails
    const supportInfo = getNotificationSupportInfo();
    
    // Cas spécial Samsung Internet : recommander d'ajouter à l'écran d'accueil
    if (supportInfo.isSamsung && supportInfo.recommendStandalone) {
      return (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-300 rounded-xl p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg mb-2">📱 Samsung Internet détecté</h3>
                <p className="text-sm text-slate-700 mb-3">
                  Pour activer les notifications, ajoutez Ovrio à votre écran d'accueil !
                </p>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Share className="w-4 h-4" />
                Comment ajouter l'app :
              </p>
              <ol className="text-sm text-slate-700 space-y-2 ml-6 list-decimal">
                <li>Appuyez sur le bouton <strong>Menu</strong> ☰ (en bas de Samsung Internet)</li>
                <li>Touchez <strong>"Ajouter page à"</strong></li>
                <li>Sélectionnez <strong>"Écran d'accueil"</strong></li>
                <li>Confirmez en appuyant sur <strong>"Ajouter"</strong></li>
                <li>Une icône Ovrio apparaît sur votre écran d'accueil</li>
                <li>Ouvrez Ovrio depuis cette icône</li>
                <li>Les notifications seront alors disponibles ! 🎉</li>
              </ol>
            </div>
            
            <div className="mt-4 bg-purple-100 rounded-lg p-3">
              <p className="text-xs text-purple-800">
                <strong>Astuce :</strong> Vous pouvez aussi essayer d'activer les notifications directement, mais l'ajout à l'écran d'accueil garantit un meilleur fonctionnement.
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Cas spécial iOS : doit être ajouté à l'écran d'accueil
    if (supportInfo.needsStandalone) {
      return (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900 text-lg mb-2">📱 iPhone/iPad détecté</h3>
                <p className="text-sm text-slate-700 mb-3">
                  Sur iOS, les notifications nécessitent que l'app soit ajoutée à votre écran d'accueil.
                </p>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <Share className="w-4 h-4" />
                Comment ajouter l'app :
              </p>
              <ol className="text-sm text-slate-700 space-y-2 ml-6 list-decimal">
                <li>Appuyez sur le bouton <strong>Partager</strong> <Share className="w-4 h-4 inline" /> (en bas de Safari)</li>
                <li>Faites défiler et touchez <strong>"Sur l'écran d'accueil"</strong></li>
                <li>Appuyez sur <strong>"Ajouter"</strong> en haut à droite</li>
                <li>Ouvrez Ovrio depuis l'icône sur votre écran d'accueil</li>
                <li>Les notifications seront alors disponibles !</li>
              </ol>
            </div>
            
            <div className="mt-4 bg-blue-100 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note :</strong> iOS 16.4 ou supérieur est requis. Vérifiez votre version dans Réglages → Général → Informations.
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    // Cas général : diagnostic détaillé
    const { hasNotificationAPI, hasServiceWorker, isSecureContext } = supportInfo;
    
    let errorMsg = '⚠️ Les notifications push ne sont pas disponibles. ';
    const issues = [];
    
    if (!hasNotificationAPI) issues.push('API Notification manquante');
    if (!hasServiceWorker) issues.push('Service Worker non supporté');
    if (!isSecureContext) issues.push('HTTPS requis');
    
    if (issues.length > 0) {
      errorMsg += 'Problèmes détectés : ' + issues.join(', ');
    }
    
    return (
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm font-medium text-yellow-800 mb-3">{errorMsg}</p>
          <div className="text-xs text-yellow-700 space-y-2">
            <p className="flex items-center gap-2">
              {hasNotificationAPI ? '✅' : '❌'} API Notification : {hasNotificationAPI ? 'OK' : 'Manquante'}
            </p>
            <p className="flex items-center gap-2">
              {hasServiceWorker ? '✅' : '❌'} Service Worker : {hasServiceWorker ? 'OK' : 'Non supporté'}
            </p>
            <p className="flex items-center gap-2">
              {isSecureContext ? '✅' : '❌'} HTTPS : {isSecureContext ? 'OK' : 'Requis'}
            </p>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 mb-2">💡 Solutions possibles :</p>
          <ul className="text-sm text-blue-700 space-y-2 list-disc list-inside">
            {!isSecureContext && (
              <li className="font-semibold">Utilisez HTTPS : vérifiez que l'URL commence par https:// (pas http://)</li>
            )}
            {!hasServiceWorker && (
              <li className="font-semibold">Mettez à jour votre navigateur vers la dernière version</li>
            )}
            <li>Utilisez Chrome, Firefox, Edge, Samsung Internet ou Safari 16.4+</li>
            <li>Vérifiez les paramètres de notifications de votre navigateur</li>
            {supportInfo.isIOS && (
              <li className="font-semibold text-purple-700">iOS : Ajoutez l'app à l'écran d'accueil</li>
            )}
            {supportInfo.isSamsung && (
              <li className="font-semibold text-purple-700">Samsung Internet : Ajoutez l'app à l'écran d'accueil</li>
            )}
          </ul>
        </div>
        
        {/* Debug info */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-xs font-mono text-slate-600 space-y-1">
            <p>🌐 URL : {supportInfo.currentUrl}</p>
            <p>🔒 Protocole : {supportInfo.protocol}</p>
            <p>📱 Navigateur : {supportInfo.browserName}</p>
            <p>🏠 Standalone : {supportInfo.isStandalone ? 'Oui' : 'Non'}</p>
            <p>🔔 API Notif : {supportInfo.hasNotificationAPI ? 'Oui' : 'Non'}</p>
            <p>⚙️ Service Worker : {supportInfo.hasServiceWorker ? 'Oui' : 'Non'}</p>
            <p>🔐 Secure Context : {supportInfo.isSecureContext ? 'Oui' : 'Non'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {permission === 'granted' ? (
              <Bell className="w-6 h-6 text-green-600" />
            ) : (
              <BellOff className="w-6 h-6 text-slate-400" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">Notifications push</h3>
              <p className="text-sm text-slate-600">
                {permission === 'granted' && 'Les notifications sont activées'}
                {permission === 'denied' && 'Les notifications sont désactivées'}
                {permission === 'default' && 'Les notifications ne sont pas configurées'}
              </p>
            </div>
          </div>

          {permission !== 'granted' && (
            <button
              onClick={handleEnableNotifications}
              disabled={isLoading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isLoading ? 'Activation...' : 'Activer'}
            </button>
          )}

          {permission === 'granted' && (
            <button
              onClick={handleDisableNotifications}
              disabled={isLoading}
              className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-100 transition disabled:opacity-50"
            >
              Désactiver
            </button>
          )}
        </div>

        {permission === 'granted' && (
          <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Types de notifications</h4>
            
            {[
              { key: 'documentSigned', label: '✍️ Documents signés', desc: 'Quand un client signe un devis ou une facture' },
              { key: 'newDocument', label: '📄 Nouveaux documents', desc: 'Création de devis, factures, chantiers' },
              { key: 'siteCreated', label: '🏗️ Nouveaux chantiers', desc: 'Quand un nouveau chantier est créé' },
              { key: 'eventCreated', label: '📅 Nouveaux rendez-vous', desc: 'Quand un rendez-vous est ajouté à l\'agenda' },
              { key: 'eventReminder', label: '⏰ Rappels de rendez-vous', desc: 'Rappels avant les rendez-vous (24h avant)' },
              { key: 'invoiceDue', label: '💰 Factures en retard', desc: 'Rappels pour les paiements en retard' },
              { key: 'teamMessages', label: '💬 Messages d\'équipe', desc: 'Notifications des membres de l\'équipe' }
            ].map(({ key, label, desc }) => (
              <label key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{label}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences[key]}
                  onChange={() => togglePreference(key)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </label>
            ))}
          </div>
        )}
      </div>

      {permission === 'denied' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800 mb-2">
            <strong>Les notifications sont bloquées par votre navigateur.</strong>
          </p>
          <p className="text-xs text-red-700">
            Pour les réactiver, cliquez sur l'icône de cadenas dans la barre d'adresse, puis autorisez les notifications pour ce site.
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationPrompt;
